import { Component, HostListener, Input } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { SeminarDateSeriesListModel } from '../../../../models/seminar.model';
import { TrainingCategoryModel } from '../../../../models/training-category.model';
import {
  getSeminarDates,
  getSeminarDateString,
  getSeminarLocationString,
  getSeminarOccupancyColor,
} from '../../../../utils/seminar.utils';
import { Filter, TableColumn } from '../../custom-table/custom-table.component';

@Component({
  selector: 'app-seminar-table',
  templateUrl: './seminar-table.component.html',
  styleUrl: './seminar-table.component.scss',
  standalone: false,
})
export class SeminarTableComponent {
  @Input() dataSource$: Observable<any[]>;
  @Input() isLoading = false;
  @Input() seminarCategory: TrainingCategoryModel;
  @Input() pageSizeOptions: number[] = [25, 50, 100];
  @Input() pageSize = 25;

  public mobileView = false;

  @HostListener('window:resize')
  onResize() {
    this.mobileView = window.innerWidth < 768;
    this.initColumns();
  }
  ngOnInit() {}

  constructor(private router: Router) {
    this.mobileView = window.innerWidth < 768;
    this.initColumns();
  }

  public columns: TableColumn[] = [];

  private initColumns() {
    if (this.mobileView) {
      // Define columns for mobile view
      this.columns = [
        {
          columnDef: 'status',
          header: 'Status',
          width: '10%',
          cellAlign: 'center',
          render: row => {
            const seminarStatusColor = getSeminarOccupancyColor(row);
            const statusClass = `inwi-status-circle-${seminarStatusColor}`;
            return `<div class="${statusClass}"></div>`;
          },
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'details',
          header: 'Details',
          width: '90%',
          fontWeight: '400',
          render: row => {
            const dates = `<div>${getSeminarDateString(row)}</div>`;
            const title = `<div><strong>${row.title}</strong></div>`;
            const location = `<div>Ort: ${getSeminarLocationString(row, false, true)}</div>`;
            const lecturer = `<div>Dozierende: ${row.lecturer}</div>`;
            return `${dates}${title}${location}${lecturer}`;
          },
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'fobiPoints',
          header: 'FP',
          headerAlign: 'center',
          cellAlign: 'center',
          width: '10%',
          fontWeight: '400',
          onCellClick: this.onCellClick.bind(this),
        },
      ];
    } else {
      // Define columns for desktop view
      this.columns = [
        {
          columnDef: 'status',
          header: 'Status',
          width: '10%',
          cellAlign: 'left',
          render: row => {
            const seminarStatusColor = getSeminarOccupancyColor(row);
            const statusClass = `inwi-chip-text-${seminarStatusColor}-outline`;
            return `<div class="${statusClass}">${row.status}</div>`;
          },
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'title',
          header: 'Inhalt',
          width: '25%',
          sortable: true,
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'dates',
          header: 'Datum',
          width: '15%',
          filterValue: row => getSeminarDates(row),
          render: row => {
            return getSeminarDateString(row);
          },
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'lecturer',
          header: 'Dozierende',
          width: '15%',
          fontWeight: '400',
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'fobiPoints',
          header: 'FP',
          headerAlign: 'center',
          cellAlign: 'center',
          width: '10%',
          fontWeight: '400',
          onCellClick: this.onCellClick.bind(this),
        },
        {
          columnDef: 'modality',
          header: 'Ort',
          width: '25%',
          fontWeight: '400',
          tooltip: row => ({
            tooltipText:
              row.seminar?.modality === 'online'
                ? ''
                : getSeminarLocationString(row, true),
            position: 'top',
            autoHide: false,
          }),
          filterValue: row =>
            row.seminar?.modality === 'online'
              ? 'online'
              : getSeminarLocationString(row, false, true),
          render: row => {
            if (row.seminar?.modality === 'online') {
              return `<div class="flex-r gap-4 align-center"><i class="p-5 fa-regular fa-globe"></i> Online</div>`;
            }
            return `<div class="flex-r gap-4 align-center"><i class="p-5 fa-regular fa-map-marker-alt"></i> ${getSeminarLocationString(row, false, true)}</div>`;
          },
        },
      ];
    }
  }

  // Define filter configuration
  public filters: Filter[] = [
    {
      filteredColumn: 'dates',
      type: 'daterange',
    },
    {
      filteredColumn: 'seminar.modality',
      type: 'select',
      selectOptions: [
        {
          value: 'online',
          label: 'Online',
        },
        {
          value: 'offline',
          label: 'Vor Ort',
        },
      ],
      placeholder: 'Ort auswählen...',
      label: 'Ort',
    },
  ];

  // Define the function that will be passed to the table component
  public onCellClick(row: SeminarDateSeriesListModel, column: any) {
    const navigationParams = [
      '/seminars',
      this.seminarCategory ? btoa(String(this.seminarCategory.id)) : undefined,
      btoa(String(row.seminar.id)),
      btoa(String(row.dateSeriesId)),
    ].filter(param => param !== undefined);

    this.router.navigate(navigationParams);
  }
}
